// ModalEstadoCuenta.jsx
import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Stack,
  Button,
  Typography,
  LinearProgress,
  Checkbox,
  Divider
} from '@mui/material';

const hoyISO = () => new Date().toISOString().slice(0, 10);

const ModalEstadoCuenta = ({
  show,
  onClose,
  tercero, // { id, tipo, nombre }
  desde,
  hasta,
  soloPendientes = false
}) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const pdfUrlRef = useRef(null);

  const [cargandoPdf, setCargandoPdf] = useState(false);
  const [enviando, setEnviando] = useState(false);

  // "movimientos" | "pendientes" | "notas_credito" | "comprobantes_pago" | "resumen_cartera"
  const [tipoReporte, setTipoReporte] = useState(soloPendientes ? 'pendientes' : 'movimientos');

  // ====== Estado para Notas de Crédito ======
  const [ncLista, setNcLista] = useState([]);
  const [ncCargando, setNcCargando] = useState(false);
  const [ncSeleccionadas, setNcSeleccionadas] = useState([]);
  const [ncIdVista, setNcIdVista] = useState(null);

  // ====== Estado para Comprobantes de pago/cobro ======
  const [cpLista, setCpLista] = useState([]);
  const [cpCargando, setCpCargando] = useState(false);
  const [cpSeleccionadas, setCpSeleccionadas] = useState([]);
  const [cpIdVista, setCpIdVista] = useState(null);
  const [descargandoExcel, setDescargandoExcel] = useState(false);

  const limpiarPdf = useCallback(() => {
    if (pdfUrlRef.current) {
      URL.revokeObjectURL(pdfUrlRef.current);
      pdfUrlRef.current = null;
    }
    setPdfUrl(null);
  }, []);

  const setPdfBlob = useCallback(
    (blob) => {
      limpiarPdf();
      const url = URL.createObjectURL(blob);
      pdfUrlRef.current = url;
      setPdfUrl(url);
    },
    [limpiarPdf]
  );

  // flags
  const esPendientes = tipoReporte === 'pendientes';
  const esNotasCredito = tipoReporte === 'notas_credito';
  const esComprobantes = tipoReporte === 'comprobantes_pago';
  const esResumenCartera = tipoReporte === 'resumen_cartera';
  const esEstadoCuenta = !esNotasCredito && !esComprobantes && !esResumenCartera;

  // si abren el modal, respetar el modo inicial
  useEffect(() => {
    if (show) setTipoReporte(soloPendientes ? 'pendientes' : 'movimientos');
  }, [soloPendientes, show]);

  /* ====================== Cargar PDF principal (Estado / Pendientes / Resumen) ====================== */
  useEffect(() => {
    let cancelado = false;

    const cargarPdf = async () => {
      if (!show) return;

      // NC y Comprobantes: no se cargan aquí
      if (esNotasCredito || esComprobantes) {
        limpiarPdf();
        setCargandoPdf(false);
        return;
      }

      // Resumen: NO requiere tercero
      if (!esResumenCartera && !tercero?.id) {
        limpiarPdf();
        return;
      }

      // Movimientos: requiere rango
      if (tipoReporte === 'movimientos' && (!desde || !hasta)) {
        limpiarPdf();
        return;
      }

      setCargandoPdf(true);
      try {
        let urlFetch = '';

        if (esResumenCartera) {
          const corte = hasta || hoyISO();
          // 👇 (asegúrate de crear esta ruta en backend)
          urlFetch = `/api/cartera/resumen-cartera/pdf?corte=${encodeURIComponent(
            corte
          )}&tipoMovimiento=C`;
        } else if (esPendientes) {
          const corte = hasta || hoyISO();
          const baseQuery = `idtercero=${tercero.id}&tipoMovimiento=${tercero.tipo}`;
          urlFetch = `/api/cartera/estado-cuenta-pendiente/pdf?${baseQuery}&corte=${encodeURIComponent(
            corte
          )}`;
        } else {
          const baseQuery = `idtercero=${tercero.id}&tipoMovimiento=${tercero.tipo}`;
          urlFetch = `/api/cartera/estado-cuenta/pdf?${baseQuery}&desde=${encodeURIComponent(
            desde
          )}&hasta=${encodeURIComponent(hasta)}`;
        }

        const res = await fetch(urlFetch);

        // 🔎 Si el frontend está respondiendo index.html con 200, aquí lo detectamos
        const ct = (res.headers.get('content-type') || '').toLowerCase();

        if (!res.ok) {
          const msg = await res.text().catch(() => '');
          throw new Error(msg || `HTTP ${res.status}`);
        }

        if (!ct.includes('application/pdf')) {
          const txt = await res.text().catch(() => '');
          throw new Error(
            `La respuesta no es PDF (content-type: ${ct || 'N/A'}). ` +
              (txt ? `Detalle: ${txt.slice(0, 200)}` : '')
          );
        }

        const blob = await res.blob();
        if (cancelado) return;
        setPdfBlob(blob);
      } catch (err) {
        console.error('Error al cargar PDF:', err);
        if (!cancelado) {
          limpiarPdf();
          alert(`❌ No se pudo generar el PDF.\n${err?.message || ''}`);
        }
      } finally {
        if (!cancelado) setCargandoPdf(false);
      }
    };

    cargarPdf();

    return () => {
      cancelado = true;
      // no limpiarPdf() aquí porque también se usa mientras navegas dentro del modal,
      // lo limpiamos al cerrar en otro effect
    };
  }, [
    show,
    tipoReporte,
    esNotasCredito,
    esComprobantes,
    esResumenCartera,
    esPendientes,
    tercero?.id,
    tercero?.tipo,
    desde,
    hasta,
    limpiarPdf,
    setPdfBlob
  ]);

  // Al cerrar el modal, revocar blob
  useEffect(() => {
    if (!show) limpiarPdf();
  }, [show, limpiarPdf]);

  /* ====================== Cargar lista de Notas de crédito ====================== */
  useEffect(() => {
    const cargarNotasCredito = async () => {
      if (!show || !tercero?.id) return;
      if (tipoReporte !== 'notas_credito') return;

      setNcCargando(true);
      setNcLista([]);
      setNcSeleccionadas([]);
      setNcIdVista(null);
      limpiarPdf();

      try {
        const params = new URLSearchParams();
        params.set('tipoMovimiento', tercero.tipo);
        params.set('idtercero', tercero.id);
        if (desde) params.set('desde', desde);
        if (hasta) params.set('hasta', hasta);

        const res = await fetch(`/api/cartera/notas-credito?${params.toString()}`);
        if (!res.ok) throw new Error(await res.text());

        const data = await res.json();
        setNcLista(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error al cargar notas de crédito:', err);
        alert('❌ Error al cargar las notas de crédito');
      } finally {
        setNcCargando(false);
      }
    };

    cargarNotasCredito();
  }, [show, tipoReporte, tercero?.id, tercero?.tipo, desde, hasta, limpiarPdf]);

  /* ====================== Cargar lista de Comprobantes ====================== */
  useEffect(() => {
    const cargarComprobantes = async () => {
      if (!show || !tercero?.id) return;
      if (tipoReporte !== 'comprobantes_pago') return;

      setCpCargando(true);
      setCpLista([]);
      setCpSeleccionadas([]);
      setCpIdVista(null);
      limpiarPdf();

      try {
        const params = new URLSearchParams();
        params.set('tipoMovimiento', tercero.tipo);
        params.set('idtercero', tercero.id);
        if (desde) params.set('desde', desde);
        if (hasta) params.set('hasta', hasta);

        const res = await fetch(`/api/cartera/comprobantes-pago?${params.toString()}`);
        if (!res.ok) throw new Error(await res.text());

        const data = await res.json();
        setCpLista(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error al cargar comprobantes:', err);
        alert('❌ Error al cargar los comprobantes de pago/cobro');
      } finally {
        setCpCargando(false);
      }
    };

    cargarComprobantes();
  }, [show, tipoReporte, tercero?.id, tercero?.tipo, desde, hasta, limpiarPdf]);

  /* ====================== Ver NC PDF ====================== */
  const verNotaCredito = async (idpagoNc) => {
    if (!idpagoNc || !tercero?.id) return;

    setNcIdVista(idpagoNc);
    setCargandoPdf(true);

    try {
      const params = new URLSearchParams();
      params.set('lado', tercero.tipo || 'C');
      if (tercero.tipo === 'P') params.set('proveedor_id', tercero.id);

      const res = await fetch(`/api/cartera/reporte-nc/${idpagoNc}?${params.toString()}`);
      const ct = (res.headers.get('content-type') || '').toLowerCase();
      if (!res.ok) throw new Error('Error al generar PDF de la nota de crédito');
      if (!ct.includes('application/pdf')) throw new Error('La respuesta no es PDF');

      const blob = await res.blob();
      setPdfBlob(blob);
    } catch (err) {
      console.error('Error al ver nota de crédito:', err);
      alert('❌ Error al generar la nota de crédito');
      limpiarPdf();
    } finally {
      setCargandoPdf(false);
    }
  };

  /* ====================== Ver Comprobante PDF ====================== */
  const verComprobantePago = async (idpago) => {
    if (!idpago || !tercero?.id) return;

    setCpIdVista(idpago);
    setCargandoPdf(true);

    try {
      const params = new URLSearchParams();
      params.set('lado', tercero.tipo || 'C');

      const res = await fetch(`/api/cartera/reporte-comprobante/${idpago}?${params.toString()}`);
      const ct = (res.headers.get('content-type') || '').toLowerCase();
      if (!res.ok) throw new Error('Error al generar PDF del comprobante');
      if (!ct.includes('application/pdf')) throw new Error('La respuesta no es PDF');

      const blob = await res.blob();
      setPdfBlob(blob);
    } catch (err) {
      console.error('Error al ver comprobante:', err);
      alert('❌ Error al generar el comprobante de pago/cobro');
      limpiarPdf();
    } finally {
      setCargandoPdf(false);
    }
  };

  /* ====================== Enviar por correo ====================== */
  const enviarPorCorreo = async () => {
    // Resumen: por ahora no
    if (esResumenCartera) {
      alert('ℹ️ El resumen de cartera aún no tiene envío por correo.');
      return;
    }
    if (!tercero?.id) return;

    setEnviando(true);
    try {
      if (!esNotasCredito && !esComprobantes) {
        const url = esPendientes
          ? `/api/cartera/estado-cuenta-pendiente/enviar`
          : `/api/cartera/estado-cuenta/enviar`;

        const payload = esPendientes
          ? { idtercero: tercero.id, tipoMovimiento: tercero.tipo, corte: hasta || hoyISO() }
          : { idtercero: tercero.id, tipoMovimiento: tercero.tipo, desde, hasta };

        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        const contentType = res.headers.get('content-type') || '';
        const resultado = contentType.includes('application/json')
          ? await res.json()
          : { error: await res.text() };
        if (!res.ok) throw new Error(resultado.error || 'No se pudo enviar el correo');

        const aceptados = Array.isArray(resultado?.entrega?.acceptedPrimary)
          ? resultado.entrega.acceptedPrimary.join(', ')
          : '';
        const messageId = resultado?.entrega?.messageId || '';
        alert(
          `Correo aceptado por el servidor de destino.${
            aceptados ? `\nDestinatario: ${aceptados}` : ''
          }${messageId ? `\nID del mensaje: ${messageId}` : ''}`
        );
        return;
      }

      if (esNotasCredito) {
        if (!ncSeleccionadas.length) {
          alert('Selecciona al menos una nota de crédito para enviar.');
          return;
        }

        const esCliente = String(tercero.tipo || '').toUpperCase() === 'C';
        const target = esCliente ? 'cliente' : 'proveedores';

        let ok = 0;
        let fail = 0;

        for (const idpago of ncSeleccionadas) {
          const params = new URLSearchParams();
          params.set('target', target);
          if (!esCliente) params.set('proveedor_id', tercero.id);

          const r = await fetch(`/api/cartera/reporte-nc/${idpago}/enviar?${params.toString()}`, {
            method: 'POST'
          });

          if (r.ok) ok++;
          else fail++;
        }

        if (ok && !fail) alert(`📩 Se enviaron correctamente ${ok} nota(s) de crédito.`);
        else if (ok && fail) alert(`📩 Se enviaron ${ok}, ${fail} tuvieron error.`);
        else throw new Error('No se pudo enviar ninguna nota de crédito');
        return;
      }

      if (esComprobantes) {
        if (!cpSeleccionadas.length) {
          alert('Selecciona al menos un comprobante para enviar.');
          return;
        }

        const esCliente = String(tercero.tipo || '').toUpperCase() === 'C';
        const target = esCliente ? 'cliente' : 'proveedores';

        let ok = 0;
        let fail = 0;

        for (const idpago of cpSeleccionadas) {
          const params = new URLSearchParams();
          params.set('target', target);

          const r = await fetch(
            `/api/cartera/reporte-comprobante/${idpago}/enviar?${params.toString()}`,
            { method: 'POST' }
          );

          if (r.ok) ok++;
          else fail++;
        }

        if (ok && !fail) alert(`📩 Se enviaron correctamente ${ok} comprobante(s).`);
        else if (ok && fail) alert(`📩 Se enviaron ${ok}, ${fail} tuvieron error.`);
        else throw new Error('No se pudo enviar ningún comprobante');
      }
    } catch (err) {
      console.error(err);
      alert(err.message || '❌ Error al enviar por correo');
    } finally {
      setEnviando(false);
    }
  };

  const descargarExcel = async () => {
    if (esResumenCartera) {
      alert('ℹ️ El resumen de cartera aún no tiene Excel.');
      return;
    }

    if (esNotasCredito || esComprobantes) {
      alert('ℹ️ Este tipo de reporte no tiene Excel.');
      return;
    }

    if (!tercero?.id) return;

    if (!esPendientes && (!desde || !hasta)) {
      alert('Debes seleccionar un rango de fechas.');
      return;
    }

    setDescargandoExcel(true);

    try {
      const params = new URLSearchParams();
      params.set('idtercero', tercero.id);
      params.set('tipoMovimiento', tercero.tipo);

      let url = '';

      if (esPendientes) {
        params.set('corte', hasta || hoyISO());
        url = `/api/cartera/estado-cuenta-pendiente/excel?${params.toString()}`;
      } else {
        params.set('desde', desde);
        params.set('hasta', hasta);
        url = `/api/cartera/estado-cuenta/excel?${params.toString()}`;
      }

      const res = await fetch(url);
      if (!res.ok) {
        const msg = await res.text().catch(() => '');
        throw new Error(msg || `HTTP ${res.status}`);
      }

      const blob = await res.blob();

      const disposition = res.headers.get('content-disposition') || '';
      let filename = esPendientes ? 'FacturasPendientes.xlsx' : 'EstadoDeCuenta.xlsx';

      const matchUtf8 = disposition.match(/filename\*=UTF-8''([^;]+)/i);
      const matchNormal = disposition.match(/filename="([^"]+)"/i);

      if (matchUtf8?.[1]) filename = decodeURIComponent(matchUtf8[1]);
      else if (matchNormal?.[1]) filename = matchNormal[1];

      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();

      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
    } catch (err) {
      console.error('Error al descargar Excel:', err);
      alert(`❌ No se pudo descargar el Excel.\n${err?.message || ''}`);
    } finally {
      setDescargandoExcel(false);
    }
  };

  // ✅ NO retornes antes de hooks / cálculos
  const terceroSafe = tercero || null;
  const tipoTercero = String(terceroSafe?.tipo || '').toUpperCase();

  let etiquetaRango = '';
  if (esResumenCartera) {
    etiquetaRango = `Corte al ${hasta || hoyISO()}`;
  } else if (esPendientes) {
    etiquetaRango = `Pendientes al ${hasta || hoyISO()}`;
  } else if (esNotasCredito) {
    const base =
      tipoTercero === 'C' ? 'Notas de crédito del cliente' : 'Notas de crédito del proveedor';
    etiquetaRango = desde && hasta ? `${base} • ${desde} a ${hasta}` : base;
  } else if (esComprobantes) {
    const base = tipoTercero === 'C' ? 'Comprobantes de cobro' : 'Comprobantes de pago';
    etiquetaRango = desde && hasta ? `${base} • ${desde} a ${hasta}` : base;
  } else {
    etiquetaRango = desde && hasta ? `${desde} a ${hasta}` : '';
  }

  // ✅ AHORA sí puedes hacer returns tempranos (ya no hay hooks debajo)
  if (!show) return null;
  if (!esResumenCartera && !terceroSafe) return null;

  const labelNc =
    String(tercero?.tipo || '').toUpperCase() === 'C'
      ? 'Notas de crédito cliente'
      : 'Notas de crédito proveedor';

  const labelComprobantes =
    String(tercero?.tipo || '').toUpperCase() === 'C'
      ? 'Comprobantes de cobro'
      : 'Comprobantes de pago';

  const deshabilitarEnviar =
    enviando ||
    esResumenCartera ||
    (esEstadoCuenta && !esPendientes && (!desde || !hasta)) ||
    (esNotasCredito && !ncSeleccionadas.length) ||
    (esComprobantes && !cpSeleccionadas.length);

  const deshabilitarExcel =
    descargandoExcel ||
    esResumenCartera ||
    esNotasCredito ||
    esComprobantes ||
    (esEstadoCuenta && !esPendientes && (!desde || !hasta));

  const toggleSeleccionNc = (idpago) => {
    setNcSeleccionadas((prev) =>
      prev.includes(idpago) ? prev.filter((x) => x !== idpago) : [...prev, idpago]
    );
  };

  const toggleSeleccionCp = (idpago) => {
    setCpSeleccionadas((prev) =>
      prev.includes(idpago) ? prev.filter((x) => x !== idpago) : [...prev, idpago]
    );
  };

  return (
    <Dialog
      open={show}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      PaperProps={{
        sx: {
          width: '95vw',
          height: '90vh',
          maxWidth: '95vw',
          maxHeight: '90vh',
          display: 'grid',
          gridTemplateRows: 'auto 1fr auto',
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          spacing={1.5}
        >
          <Box>
            <Typography variant="h6">
              {esResumenCartera
                ? '📄 Resumen de Cartera (Aging)'
                : esNotasCredito
                  ? '📄 Reporte de Notas de Crédito'
                  : esPendientes
                    ? '📄 Estado de Cuenta - Pendientes'
                    : esComprobantes
                      ? '📄 Comprobantes de pago / cobro'
                      : '📄 Estado de Cuenta'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {esResumenCartera ? 'Resumen cartera' : tercero?.nombre || 'SIN NOMBRE'} •{' '}
              {etiquetaRango}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2">Tipo de reporte:</Typography>
            <select
              id="tipo-reporte"
              value={tipoReporte}
              onChange={(e) => setTipoReporte(e.target.value)}
              style={{
                padding: '4px 8px',
                borderRadius: 4,
                border: '1px solid #ccc',
                fontSize: 14
              }}
            >
              <option value="movimientos">Movimientos estado de cuenta</option>
              <option value="pendientes">Estado de cuenta pendientes</option>
              <option value="resumen_cartera">Resumen de cartera (Aging)</option>
              {!!tercero && <option value="notas_credito">{labelNc}</option>}
              {!!tercero && <option value="comprobantes_pago">{labelComprobantes}</option>}
            </select>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}>
        {cargandoPdf && <LinearProgress sx={{ mb: 1 }} />}

        {esNotasCredito || esComprobantes ? (
          <Box sx={{ display: 'flex', height: '100%', minHeight: 0, gap: 2 }}>
            {/* LISTA */}
            <Box
              sx={{
                width: 350,
                flexShrink: 0,
                borderRight: '1px solid',
                borderColor: 'divider',
                pr: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {esNotasCredito
                  ? `Notas de crédito de este ${
                      String(tercero?.tipo || '').toUpperCase() === 'C' ? 'cliente' : 'proveedor'
                    }`
                  : `Comprobantes de ${
                      String(tercero?.tipo || '').toUpperCase() === 'C' ? 'cobro' : 'pago'
                    }`}
              </Typography>
              <Divider sx={{ mb: 1 }} />

              <Box sx={{ flex: 1, overflow: 'auto' }}>
                {esNotasCredito ? (
                  ncCargando ? (
                    <Box sx={{ p: 1, fontSize: 13 }}>Cargando notas de crédito...</Box>
                  ) : !ncLista.length ? (
                    <Box sx={{ p: 1, fontSize: 13, color: 'text.secondary' }}>
                      No hay notas de crédito registradas.
                    </Box>
                  ) : (
                    ncLista.map((nc) => {
                      const seleccionado = ncSeleccionadas.includes(nc.idpago);
                      const viendo = ncIdVista === nc.idpago;
                      return (
                        <Box
                          key={nc.idpago}
                          onClick={() => verNotaCredito(nc.idpago)}
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1,
                            p: 1,
                            mb: 0.5,
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: viendo ? 'primary.main' : 'divider',
                            bgcolor: viendo ? 'action.hover' : 'background.paper',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' }
                          }}
                        >
                          <Checkbox
                            size="small"
                            checked={seleccionado}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => toggleSeleccionNc(nc.idpago)}
                          />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle2" noWrap>
                              NC #{nc.numero_nc || nc.idpago} • {nc.fecha}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" noWrap>
                              {nc.nombre || 'SIN NOMBRE'}
                            </Typography>
                            <Typography
                              variant="caption"
                              color={nc.correo ? 'text.secondary' : 'error.main'}
                              noWrap
                            >
                              {nc.correo || 'Sin correo registrado'}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                              Valor:{' '}
                              {Number(nc.valor || 0).toLocaleString('es-EC', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })
                  )
                ) : cpCargando ? (
                  <Box sx={{ p: 1, fontSize: 13 }}>Cargando comprobantes...</Box>
                ) : !cpLista.length ? (
                  <Box sx={{ p: 1, fontSize: 13, color: 'text.secondary' }}>
                    No hay comprobantes registrados.
                  </Box>
                ) : (
                  cpLista.map((cp) => {
                    const seleccionado = cpSeleccionadas.includes(cp.idpago);
                    const viendo = cpIdVista === cp.idpago;
                    return (
                      <Box
                        key={cp.idpago}
                        onClick={() => verComprobantePago(cp.idpago)}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1,
                          p: 1,
                          mb: 0.5,
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: viendo ? 'primary.main' : 'divider',
                          bgcolor: viendo ? 'action.hover' : 'background.paper',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        <Checkbox
                          size="small"
                          checked={seleccionado}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleSeleccionCp(cp.idpago)}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle2" noWrap>
                            {cp.numero_comprobante
                              ? `CH ${cp.numero_comprobante} • ${cp.fecha}`
                              : `Pago #${cp.idpago} • ${cp.fecha}`}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {cp.nombre || 'SIN NOMBRE'}
                          </Typography>
                          <Typography
                            variant="caption"
                            color={cp.correo ? 'text.secondary' : 'error.main'}
                            noWrap
                          >
                            {cp.correo || 'Sin correo registrado'}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                            Valor:{' '}
                            {Number(cp.valor || 0).toLocaleString('es-EC', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })
                )}
              </Box>
            </Box>

            {/* VISOR PDF */}
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              {cargandoPdf ? (
                <Box sx={{ p: 2, fontSize: 14 }}>Generando PDF...</Box>
              ) : pdfUrl ? (
                <Box
                  component="iframe"
                  src={pdfUrl}
                  title={esNotasCredito ? 'PDF Nota de Crédito' : 'PDF Comprobante'}
                  sx={{
                    width: '100%',
                    height: '100%',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1
                  }}
                />
              ) : (
                <Box sx={{ p: 2, fontSize: 14, color: 'text.secondary' }}>
                  {esNotasCredito
                    ? 'Selecciona una nota de crédito de la lista para ver el PDF.'
                    : 'Selecciona un comprobante de la lista para ver el PDF.'}
                </Box>
              )}
            </Box>
          </Box>
        ) : (
          <Box
            sx={{ flex: 1, minHeight: 0, height: '100%', display: 'flex', flexDirection: 'column' }}
          >
            {cargandoPdf ? (
              <Box sx={{ p: 2, fontSize: 14 }}>Generando PDF...</Box>
            ) : pdfUrl ? (
              <Box
                component="iframe"
                src={pdfUrl}
                title={esResumenCartera ? 'PDF Resumen Cartera' : 'PDF Estado de Cuenta'}
                sx={{
                  width: '100%',
                  height: '100%',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1
                }}
              />
            ) : (
              <Box sx={{ p: 2, fontSize: 14, color: 'text.secondary' }}>
                ⚠️ No hay vista previa disponible.
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1 }}>
        <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
          {esEstadoCuenta && (
            <Button variant="outlined" onClick={descargarExcel} disabled={deshabilitarExcel}>
              📥 {descargandoExcel ? 'Descargando Excel...' : 'Descargar Excel'}
            </Button>
          )}

          <Button variant="contained" onClick={enviarPorCorreo} disabled={deshabilitarEnviar}>
            📤 {enviando ? 'Enviando...' : 'Enviar por correo'}
          </Button>

          <Button variant="text" onClick={onClose} disabled={enviando || descargandoExcel}>
            Cerrar
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default ModalEstadoCuenta;
